/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {useRef} from "react";
import type React from "react";
import {flexRender} from "@tanstack/react-table";
import type {Header} from "@tanstack/react-table";
import {clsx} from "~/components/table/tableUtils.tsx";
// import {SortIcon} from "~/components/table/SortIcon"

interface TableHeaderCellProps<TRow> {
    header: Header<TRow, unknown>
    isDragging?: boolean
    dragOverId?: string | null
    onDragStart?: (e: React.DragEvent, headerId: string) => void
    onDragOver?: (e: React.DragEvent, headerId: string) => void
    onDragEnter?: (e: React.DragEvent) => void
    onDrop?: (e: React.DragEvent, headerId: string) => void
    onDragEnd?: () => void
    canReorder?: boolean
}

export function TableHeaderCell<TRow>({
    header,
    isDragging,
    dragOverId,
    onDragStart,
    onDragOver,
    onDragEnter,
    onDrop,
    onDragEnd,
    canReorder,
}: TableHeaderCellProps<TRow>) {
    const resizingRef = useRef(false);
    const meta = header.column.columnDef.meta as Record<string, unknown> | undefined;
    const columnId = header.column.id;
    const align = (meta?.align as string) ?? "left";
    // const canSort = header.column.getCanSort()
    // const sortDir = header.column.getIsSorted()
    const canSort = false;
    const canResize = header.column.getCanResize();
    const isPinned = header.column.getIsPinned();
    const isDragOver = dragOverId === columnId;
    const isBeingDragged = isDragging;
    const renderedHeader = flexRender(header.column.columnDef.header, header.getContext());

    return (
        <th
            className={clsx(
                "table-th",
                columnId === "__checkbox__" && "table-th--checkbox",
                columnId === "__actions__" && "table-th--actions",
                isPinned === "left" && "table-th--pinned-left",
                isPinned === "right" && "table-th--pinned-right",
                isDragOver && "table-th--drag-over",
                isBeingDragged && "table-th--dragging",
                canReorder && columnId !== "__checkbox__" && columnId !== "__actions__" && "table-th--reorderable",
            )}
            style={{
                width: header.getSize(),
                minWidth: header.column.columnDef.minSize,
                textAlign: align as React.CSSProperties["textAlign"],
                ...(isPinned === "left"
                    ? {left: `${header.column.getStart("left")}px`}
                    : isPinned === "right"
                      ? {right: `${header.column.getAfter("right")}px`}
                    : {}),
            }}
            draggable={Boolean(canReorder && columnId !== "__checkbox__" && columnId !== "__actions__")}
            onDragStart={canReorder ? (e) => {
                if (resizingRef.current) {
                    e.preventDefault();
                    return;
                }
                onDragStart?.(e, columnId);
            } : undefined}
            onDragOver={canReorder ? (e) => onDragOver?.(e, columnId) : undefined}
            onDragEnter={canReorder ? (e) => onDragEnter?.(e) : undefined}
            onDrop={canReorder ? (e) => onDrop?.(e, columnId) : undefined}
            onDragEnd={canReorder ? () => {
                resizingRef.current = false;
                onDragEnd?.();
            } : undefined}
        >
            <div
                className={clsx(
                    "table-th-content",
                    canSort && "table-th-content--sortable",
                    columnId === "__checkbox__" && "table-th-content--checkbox",
                )}
                // onClick={() => canSort && header.column.toggleSorting()}
                style={columnId === "__checkbox__" ? undefined : {justifyContent: alignToFlex(align)}}
            >
                {typeof header.column.columnDef.header === "string" ? (
                    <>
                        <span className="table-th-label">{header.column.columnDef.header}</span>
                        {/* {canSort ? <SortIcon direction={sortDir} /> : null} */}
                    </>
                ) : (
                    renderedHeader
                )}
            </div>
            {canResize ? (
                <div
                    className={clsx("table-resize-handle", (header.column.getIsResizing() || resizingRef.current) && "table-resize-handle--active")}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        resizingRef.current = true;

                        const startX = e.clientX;
                        const startW = header.getSize();
                        const colId = header.column.id;
                        const minW = header.column.columnDef.minSize ?? 60;
                        const table = header.getContext().table;

                        const onMove = (mv: MouseEvent) => {
                            const delta = mv.clientX - startX;
                            const next = Math.max(minW, startW + delta);
                            table.setColumnSizing((prev) => ({
                                ...prev,
                                [colId]: next,
                            }));
                        };

                        const onUp = () => {
                            resizingRef.current = false;
                            window.removeEventListener("mousemove", onMove);
                            window.removeEventListener("mouseup", onUp);
                        };

                        window.addEventListener("mousemove", onMove);
                        window.addEventListener("mouseup", onUp);
                    }}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : null}
        </th>
    );
}

function alignToFlex(align: string): React.CSSProperties["justifyContent"] {
    if (align === "center") {return "center";}
    if (align === "right") {return "flex-end";}
    return "flex-start";
}
